import type { QUERY_ORDER } from 'elements/controller/query/query'
import { useEffect, useMemo, useRef } from 'react'
import {
  CRUD_GET_LIST,
  Identifier,
  Record,
  RecordMap,
  shouldUsePostForFilters,
  useGetList,
  useGetListWithPost,
} from 'ra-core'

/*
The basic hook for fetching hardware components data from endpoint 
*/

// Note: consider to combine this hook with useOrderComponentsListBasic

type ListAbortWiring = {
  parent: AbortSignal
  child: AbortController
  onParentAbort: () => void
  filterKey: string
  page: number
  perPage: number
  sort: string
  order: QUERY_ORDER
  resource: string
  shouldUsePost: boolean
  shouldBlockRequest: boolean
}

const useHardwareComponentsListBasic = ({
  page,
  perPage,
  sort,
  order,
  resource,
  filterValues,
  blockRequest = false,
  listAbortSignal,
}: {
  page: number
  perPage: number
  sort: string
  order: QUERY_ORDER
  resource: string
  filterValues: { [key: string]: unknown }
  blockRequest?: boolean
  listAbortSignal?: AbortSignal
}): {
  data?: RecordMap<Record>
  ids?: Identifier[]
  total?: number
  error?: any
  loading: boolean
  loaded: boolean
} => {
  const shouldUsePost = shouldUsePostForFilters(filterValues)
  // Block request if codes filter is empty (no components to look up)
  // This is useless and also causes performance issues on the BE.
  const hasEmptyCodes = filterValues.codes === ''
  const shouldBlockRequest = blockRequest || hasEmptyCodes

  const filterKey = useMemo(() => JSON.stringify(filterValues), [filterValues])

  /**
   * Child AbortSignal forwarded to GET_LIST, aborted when the shop parent signal
   * aborts or when list params/filters change (same behaviour as the previous
   * useLayoutEffect + useState wiring).
   *
   * Must be updated synchronously during render: if we only flipped the child in
   * useLayoutEffect, the first render after e.g. client-side navigation to a shop
   * supplier would still pass the *previous* child signal — already aborted when
   * {@link useShopHardwareListAbortSignal} swapped controllers. {@link useQueryWithStore}
   * omits `signal` from its request identity, so that first (aborted) fetch would be
   * the only one and the list would never reload.
   */
  const listAbortWiringRef = useRef<ListAbortWiring | null>(null)

  const teardownListAbortWiring = () => {
    const w = listAbortWiringRef.current
    if (!w) return
    w.parent.removeEventListener('abort', w.onParentAbort)
    w.child.abort()
    listAbortWiringRef.current = null
  }

  if (!listAbortSignal) {
    teardownListAbortWiring()
  } else {
    const w = listAbortWiringRef.current
    const needsRewire =
      !w ||
      w.parent !== listAbortSignal ||
      w.filterKey !== filterKey ||
      w.page !== page ||
      w.perPage !== perPage ||
      w.sort !== sort ||
      w.order !== order ||
      w.resource !== resource ||
      w.shouldUsePost !== shouldUsePost ||
      w.shouldBlockRequest !== shouldBlockRequest

    if (needsRewire) {
      teardownListAbortWiring()
      const child = new AbortController()
      const onParentAbort = () => child.abort()
      listAbortSignal.addEventListener('abort', onParentAbort)
      listAbortWiringRef.current = {
        parent: listAbortSignal,
        child,
        onParentAbort,
        filterKey,
        page,
        perPage,
        sort,
        order,
        resource,
        shouldUsePost,
        shouldBlockRequest,
      }
    }
  }

  useEffect(() => {
    return () => {
      const w = listAbortWiringRef.current
      if (!w) return
      w.parent.removeEventListener('abort', w.onParentAbort)
      w.child.abort()
      listAbortWiringRef.current = null
    }
  }, [])

  const composedListSignal = listAbortSignal ? listAbortWiringRef.current?.child.signal : undefined

  const listOptions = {
    action: CRUD_GET_LIST,
    blockRequest: shouldBlockRequest,
    ...(composedListSignal ? { signal: composedListSignal } : {}),
  }

  const postResult = useGetListWithPost(resource, { page, perPage }, { field: sort, order }, filterValues, {
    ...listOptions,
    blockRequest: listOptions.blockRequest || !shouldUsePost,
  })

  const getResult = useGetList(resource, { page, perPage }, { field: sort, order }, filterValues, {
    ...listOptions,
    blockRequest: listOptions.blockRequest || shouldUsePost,
  })

  const branchResult = shouldUsePost ? postResult : getResult

  if (shouldBlockRequest) {
    return {
      data: undefined,
      ids: [],
      total: 0,
      error: undefined,
      loading: false,
      loaded: true,
    }
  }

  return branchResult
}

export default useHardwareComponentsListBasic
